import React, { useCallback, useState } from "react";
import {
    Button as MuiButton,
    Chip,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Switch,
    TextField,
} from "@mui/material";
import styled from "styled-components";
import { AppModal } from "../../../../components/ui";
import CurrencyTextField from "../../../../components/currency-text-field";
import PlanModel from "../../../../models/plan.model";
import { daysOptions, PlanPeriodOption } from "../plans-options";

// ── feature-list chip container (unchanged from new-plan.tsx) ──────────────
const ChipContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: start;
  margin-top: 8px;
  flex-wrap: wrap;
`;

interface PlanFormProps {
    plan?: PlanModel;
    onClose: () => void;
    onConfirm: (plan: PlanModel, id?: number) => void;
}

/** Modal de cadastro/edição de plano (lógica idêntica ao antigo new-plan.tsx). */
const PlanFormModal: React.FC<PlanFormProps> = ({ plan, onClose, onConfirm }) => {
    const [name, setName] = useState<string>(plan?.name || "");
    const [value, setValue] = useState<number>(plan?.value || 0);
    const [option, setOption] = useState<PlanPeriodOption>(
        daysOptions.find((el) => plan?.daysOfUse?.toString() === el.value) || daysOptions[0]
    );
    const [newFeat, setNewFeat] = useState("");
    const [features, setFeatures] = useState<string[]>(plan?.features || []);
    const [isPreapprovalRegistered] = useState<boolean>(plan?.preapproval_enabled || false);
    const [preapproval, setPreapproval] = useState<boolean>(plan?.preapproval_enabled || false);
    const [isPromotional, setIsPromotional] = useState<boolean>(plan?.is_promotional || false);

    const handleChange = useCallback((event: SelectChangeEvent) => {
        const find = daysOptions.find((el) => el.value === event.target.value);
        setOption(find || daysOptions[0]);
    }, []);

    const onHandleConfirm = useCallback(() => {
        const payload = {
            name: name || "",
            value: value || 0,
            daysOfUse: parseInt(option.value),
            features,
            preapproval_enabled: preapproval,
            is_promotional: isPromotional,
        } as PlanModel;
        onConfirm(payload, plan?.id);
    }, [name, value, option, preapproval, features, isPromotional, onConfirm, plan?.id]);

    const handleKeyPress = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === "Enter" && newFeat.length > 0) {
                event.preventDefault();
                setFeatures([...features, newFeat]);
                setNewFeat("");
            }
        },
        [features, newFeat]
    );

    const handleChipDelete = useCallback(
        (feat: string) => {
            setFeatures(features.filter((v) => v !== feat));
        },
        [features]
    );

    const isEditing = !!plan?.id;
    const kicker = isEditing ? "Editar" : "Cadastrar";
    const title = isEditing ? "Editar plano" : "Novo plano";

    const footer = (
        <>
            <MuiButton variant="outlined" onClick={onClose}>
                Cancelar
            </MuiButton>
            <MuiButton variant="contained" onClick={onHandleConfirm}>
                Salvar
            </MuiButton>
        </>
    );

    return (
        <AppModal open onClose={onClose} kicker={kicker} title={title} footer={footer} maxWidth={480}>
            <TextField
                fullWidth
                label="Nome"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                sx={{ marginTop: "8px", width: "100%", minWidth: "320px" }}
            />

            <CurrencyTextField label="Valor total" value={value} onChange={(v) => setValue(v)} sx={{ marginTop: "16px", width: "100%", minWidth: "320px" }} />

            <FormControl sx={{ marginTop: "16px", width: "100%", minWidth: "320px" }}>
                <InputLabel id="plan-period-select">Periodo</InputLabel>
                <Select label="Periodo" value={option.value} labelId="plan-period-select" onChange={handleChange}>
                    {daysOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormGroup sx={{ mt: 1 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={preapproval}
                            disabled={isPreapprovalRegistered}
                            onChange={(e) => setPreapproval(e.target.checked as unknown as boolean)}
                            color="primary"
                        />
                    }
                    label="Pagamento recorrente?"
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={isPromotional}
                            onChange={(e) => setIsPromotional(e.target.checked as unknown as boolean)}
                            color="primary"
                        />
                    }
                    label="Plano promocional?"
                />
            </FormGroup>

            <TextField
                fullWidth
                label="Caracteristicas"
                variant="outlined"
                value={newFeat}
                onKeyPress={(e) => handleKeyPress(e)}
                onChange={(e) => setNewFeat(e.target.value)}
                sx={{ marginTop: "16px", width: "100%", minWidth: "320px" }}
            />

            <ChipContainer>
                {features.map((feat) => (
                    <Chip
                        key={feat}
                        size="small"
                        label={feat}
                        variant="filled"
                        color="primary"
                        onDelete={() => handleChipDelete(feat)}
                        style={{ marginRight: "16px", marginTop: "8px" }}
                    />
                ))}
            </ChipContainer>
        </AppModal>
    );
};

export default PlanFormModal;
